import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit, Search, Filter } from 'lucide-react';
import { useApiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const accent = '#6366f1';
const accentLight = '#f1f5ff';

const Showrooms = () => {
  const { getAllShowrooms, createShowroom, updateShowroom } = useApiService();
  const { user: currentUser } = useAuth();
  const [showrooms, setShowrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingShowroom, setEditingShowroom] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);

  const [formData, setFormData] = useState({
    showroomName: '',
    showroomAddress: '',
    showroomPhone: '',
    showroomEmail: '',
    showroomWebsite: '',
    showroomDescription: '',
    showroomLogo: '',
    showroomBanner: '',
    showroomSocialMedia: [],
    showroomOwner: '',
    NTN: '',
    whatsappNumber: '',
    facebookLink: '',
    instagramLink: '',
    twitterLink: '',
    linkedinLink: '',
    youtubeLink: '',
    googleMapLink: '',
    policyAndTerms: '',
    status: 'active',
    storage: '',
    storageLeft: '',
    maxUsers: ''
  });

  useEffect(() => {
    loadShowrooms();
  }, []);

  const loadShowrooms = async () => {
    try {
      setLoading(true);
      const response = await getAllShowrooms();
      setShowrooms(response || []);
      setError(null);
    } catch (err) {
      console.error('Error loading showrooms:', err);
      setError('Failed to load showrooms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Function to extract original filename from S3 key
  const getOriginalFilename = (s3Key) => {
    if (!s3Key) return '';
    // Remove timestamp prefix and "uploads/" folder
    // Format: uploads/1703123456789-filename.jpg
    const parts = s3Key.split('/');
    if (parts.length >= 2) {
      const filenameWithTimestamp = parts[parts.length - 1];
      const timestampParts = filenameWithTimestamp.split('-');
      if (timestampParts.length >= 2) {
        // Remove timestamp and return original filename
        return timestampParts.slice(1).join('-');
      }
    }
    return s3Key; // Fallback to full key if parsing fails
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (type === 'logo') {
        setLogoFile(file);
        setFormData(prev => ({ ...prev, showroomLogo: '' }));
      } else if (type === 'banner') {
        setBannerFile(file);
        setFormData(prev => ({ ...prev, showroomBanner: '' }));
      }
    }
  };

  const uploadFileToS3 = async (file, type) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      console.log(`Uploading ${type} file:`, file.name, 'Size:', file.size);
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3020'}/api/files/upload`, {
        method: 'POST',
        body: formData,
      });

      console.log(`Upload response status:`, response.status);
      console.log(`Upload response headers:`, response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Upload failed with status ${response.status}:`, errorText);
        throw new Error(`File upload failed: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log(`Upload successful:`, result);
      return result.data.key; // Return the S3 key
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      throw error;
    }
  };

  const handleAddShowroom = async (e) => {
    e.preventDefault();
    if (!formData.showroomName.trim()) {
      alert('Showroom name is required!');
      return;
    }
    try {
      setSubmitting(true);
      
      // Upload logo if selected
      if (logoFile) {
        setUploadingLogo(true);
        try {
          const logoKey = await uploadFileToS3(logoFile, 'logo');
          formData.showroomLogo = logoKey;
        } catch (error) {
          alert(`Failed to upload logo: ${error.message}`);
          setUploadingLogo(false);
          return;
        }
        setUploadingLogo(false);
      }

      // Upload banner if selected
      if (bannerFile) {
        setUploadingBanner(true);
        try {
          const bannerKey = await uploadFileToS3(bannerFile, 'banner');
          formData.showroomBanner = bannerKey;
        } catch (error) {
          alert(`Failed to upload banner: ${error.message}`);
          setUploadingBanner(false);
          return;
        }
        setUploadingBanner(false);
      }

      await createShowroom(formData);
      await loadShowrooms();
      
      // Reset form and files
      setFormData({
        showroomName: '', showroomAddress: '', showroomPhone: '', showroomEmail: '',
        showroomWebsite: '', showroomDescription: '', showroomLogo: '', showroomBanner: '',
        showroomSocialMedia: [], showroomOwner: '', NTN: '', whatsappNumber: '',
        facebookLink: '', instagramLink: '', twitterLink: '', linkedinLink: '',
        youtubeLink: '', googleMapLink: '', policyAndTerms: '', status: 'active'
      });
      setLogoFile(null);
      setBannerFile(null);
      setShowAddModal(false);
      alert('Showroom created successfully!');
    } catch (err) {
      console.error('Error creating showroom:', err);
      alert('Failed to create showroom. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditShowroom = async (e) => {
    e.preventDefault();
    if (!formData.showroomName.trim()) {
      alert('Showroom name is required!');
      return;
    }
    try {
      setSubmitting(true);
      
      // Upload logo if selected
      if (logoFile) {
        setUploadingLogo(true);
        try {
          const logoKey = await uploadFileToS3(logoFile, 'logo');
          formData.showroomLogo = logoKey;
        } catch (error) {
          alert(`Failed to upload logo: ${error.message}`);
          setUploadingLogo(false);
          return;
        }
        setUploadingLogo(false);
      }

      // Upload banner if selected
      if (bannerFile) {
        setUploadingBanner(true);
        try {
          const bannerKey = await uploadFileToS3(bannerFile, 'banner');
          formData.showroomBanner = bannerKey;
        } catch (error) {
          alert(`Failed to upload banner: ${error.message}`);
          setUploadingBanner(false);
          return;
        }
        setUploadingBanner(false);
      }

      // Prepare data for update, converting storage values to numbers
      const updateData = { ...formData };
      if (updateData.storage && updateData.storage !== '') {
        updateData.storage = parseFloat(updateData.storage);
      }
      if (updateData.storageLeft && updateData.storageLeft !== '') {
        updateData.storageLeft = parseFloat(updateData.storageLeft);
      }
      if (updateData.maxUsers && updateData.maxUsers !== '') {
        updateData.maxUsers = parseInt(updateData.maxUsers);
      }

      await updateShowroom(editingShowroom._id || editingShowroom.id, updateData);
      await loadShowrooms();
      
      // Reset form and files
      setFormData({
        showroomName: '', showroomAddress: '', showroomPhone: '', showroomEmail: '',
        showroomWebsite: '', showroomDescription: '', showroomLogo: '', showroomBanner: '',
        showroomSocialMedia: [], showroomOwner: '', NTN: '', whatsappNumber: '',
        facebookLink: '', instagramLink: '', twitterLink: '', linkedinLink: '',
        youtubeLink: '', googleMapLink: '', policyAndTerms: '', status: 'active',
        storage: '', storageLeft: ''
      });
      setLogoFile(null);
      setBannerFile(null);
      setShowEditModal(false);
      setEditingShowroom(null);
      alert('Showroom updated successfully!');
    } catch (err) {
      console.error('Error updating showroom:', err);
      alert('Failed to update showroom. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (showroom) => {
    setEditingShowroom(showroom);
    setFormData({
      showroomName: showroom.showroomName || '',
      showroomAddress: showroom.showroomAddress || '',
      showroomPhone: showroom.showroomPhone || '',
      showroomEmail: showroom.showroomEmail || '',
      showroomWebsite: showroom.showroomWebsite || '',
      showroomDescription: showroom.showroomDescription || '',
      showroomLogo: showroom.showroomLogo || '',
      showroomBanner: showroom.showroomBanner || '',
      showroomSocialMedia: showroom.showroomSocialMedia || [],
      showroomOwner: showroom.showroomOwner || '',
      NTN: showroom.NTN || '',
      whatsappNumber: showroom.whatsappNumber || '',
      facebookLink: showroom.facebookLink || '',
      instagramLink: showroom.instagramLink || '',
      twitterLink: showroom.twitterLink || '',
      linkedinLink: showroom.linkedinLink || '',
      youtubeLink: showroom.youtubeLink || '',
      googleMapLink: showroom.googleMapLink || '',
      policyAndTerms: showroom.policyAndTerms || '',
      status: showroom.status || 'active',
      storage: showroom.storage !== undefined && showroom.storage !== null ? parseFloat(showroom.storage).toFixed(3) : '20480.000',
      storageLeft: showroom.storageLeft !== undefined && showroom.storageLeft !== null ? parseFloat(showroom.storageLeft).toFixed(3) : '20480.000',
      maxUsers: showroom.maxUsers !== undefined && showroom.maxUsers !== null ? showroom.maxUsers.toString() : '2'
    });
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setFormData({
      showroomName: '', showroomAddress: '', showroomPhone: '', showroomEmail: '',
      showroomWebsite: '', showroomDescription: '', showroomLogo: '', showroomBanner: '',
      showroomSocialMedia: [], showroomOwner: '', NTN: '', whatsappNumber: '',
      facebookLink: '', instagramLink: '', twitterLink: '', linkedinLink: '',
      youtubeLink: '', googleMapLink: '', policyAndTerms: '', status: 'active',
      storage: '', storageLeft: '', maxUsers: ''
    });
    setLogoFile(null);
    setBannerFile(null);
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingShowroom(null);
  };

  const filteredShowrooms = showrooms.filter(showroom => 
    showroom.showroomName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    showroom.showroomAddress?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '16rem' }}>
          <div style={{ 
            width: '3rem', 
            height: '3rem', 
            border: '2px solid #e5e7eb', 
            borderTop: '2px solid #6366f1', 
            borderRadius: '50%', 
            animation: 'spin 1s linear infinite' 
          }}></div>
        </div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
      background: '#fafbfc', 
      minHeight: '100vh',
      padding: '2rem 1.5rem',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      {/* Header Section */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '.5rem' }}>
        <div>
          <h1 className="fw-bold mb-2" style={{ color: '#1f2937', fontSize: '1.75rem', letterSpacing: '-0.025em' }}>Showrooms Management</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>Manage all showrooms and their configurations</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold"
            style={{ background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.875rem' }}
          >
            <Filter size={16} />
            <span className="d-none d-sm-inline">Filter</span>
          </button>
          <button 
            className="btn d-flex align-items-center justify-content-center gap-2 px-3 px-md-4 py-2 fw-semibold"
            style={{ background: accent, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.875rem', boxShadow: '0 1px 3px 0 rgba(99, 102, 241, 0.1), 0 1px 2px 0 rgba(99, 102, 241, 0.06)' }}
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} />
            <span className="d-none d-sm-inline">Add Showroom</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', marginBottom: '1.5rem' }}>
        <div className="card-body p-3 p-md-4">
          <div className="d-flex align-items-center gap-3">
            <div style={{ position: 'relative', flex: '1' }}>
              <Search style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} size={20} />
              <input
                type="text"
                className="form-control"
                placeholder="Search showrooms by name or address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', paddingLeft: '48px', fontSize: '0.875rem' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      {/* Showrooms List */}
      <div className="card border-0 shadow-sm" style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden' }}>
        <div className="table-responsive">
          <table className="table mb-0">
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th className="border-0 fw-semibold text-muted py-2 px-2" style={{ fontSize: '0.875rem' }}>Showroom</th>
                <th className="border-0 fw-semibold text-muted py-2 d-none d-md-table-cell" style={{ fontSize: '0.875rem' }}>Contact</th>
                <th className="border-0 fw-semibold text-muted py-2" style={{ fontSize: '0.875rem' }}>Status</th>
                <th className="border-0 fw-semibold text-muted py-2 d-none d-lg-table-cell" style={{ fontSize: '0.875rem' }}>Storage (MB)</th>
                <th className="border-0 fw-semibold text-muted py-2 d-none d-lg-table-cell" style={{ fontSize: '0.875rem' }}>Storage Left (MB)</th>
                <th className="border-0 fw-semibold text-muted py-2 d-none d-xl-table-cell" style={{ fontSize: '0.875rem' }}>Max Users</th>
                <th className="border-0 fw-semibold text-muted py-2 text-end" style={{ fontSize: '0.875rem' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredShowrooms.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5">
                    <Building2 size={48} style={{ margin: '0 auto 1rem', color: '#9ca3af', display: 'block' }} />
                    <h5 style={{ color: '#374151', margin: '0 0 0.5rem 0' }}>No showrooms found</h5>
                    <p style={{ color: '#6b7280', margin: '0 0 1rem 0' }}>
                      {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by creating your first showroom.'}
                    </p>
                    {!searchTerm && (
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowAddModal(true)}
                        style={{ background: accent, border: 'none' }}
                      >
                        Create Showroom
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredShowrooms.map((showroom) => (
                  <tr key={showroom._id || showroom.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td className="py-2">
                                              <div className="d-flex align-items-center gap-2 gap-md-3">
                        <div style={{ width: 48, height: 48, background: accentLight, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden', border: '2px solid #e5e7eb' }}>
                          {showroom.showroomLogo ? (
                            <>
                              <img 
                                src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3020'}/api/files/serve/${showroom.showroomLogo}`}
                                alt="Logo" 
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              <div style={{ display: 'none', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                <Building2 size={20} color={accent} />
                              </div>
                            </>
                          ) : (
                            <Building2 size={20} color={accent} />
                          )}
                        </div>
                        {/* {showroom.showroomLogo && (
                          <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.25rem' }}>
                            <div style={{ marginBottom: '0.25rem', fontSize: '0.65rem' }}>
                              <strong>File:</strong> {getOriginalFilename(showroom.showroomLogo)}
                            </div>
                            <button 
                              onClick={async () => {
                                try {
                                  const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3020'}/api/files/presigned-download-url/${showroom.showroomLogo}`);
                                  const data = await response.json();
                                  if (data.presignedUrl) {
                                    window.open(data.presignedUrl, '_blank');
                                  }
                                } catch (error) {
                                  console.error('Error getting logo URL:', error);
                                  alert('Failed to open logo');
                                }
                              }}
                              style={{ 
                                color: '#6366f1', 
                                textDecoration: 'none',
                                fontSize: '0.65rem',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0
                              }}
                            >
                              🔗 View Logo
                            </button>
                          </div>
                        )} */}
                        <div className="flex-grow-1 min-w-0">
                          <div className="fw-semibold text-truncate" style={{ color: '#1f2937', fontSize: '0.875rem' }}>
                            {showroom.showroomName}
                          </div>
                          <div className="text-muted d-none d-md-block" style={{ fontSize: '0.75rem' }}>
                            {showroom.showroomAddress || 'No address'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 d-none d-md-table-cell">
                      <div style={{ fontSize: '0.875rem' }}>
                        {showroom.showroomPhone && <div>📞 {showroom.showroomPhone}</div>}
                        {showroom.showroomEmail && <div>✉️ {showroom.showroomEmail}</div>}
                        {showroom.whatsappNumber && <div>💬 {showroom.whatsappNumber}</div>}
                      </div>
                    </td>
                    <td className="py-2">
                      <span className={`badge px-2 py-1 px-md-3 py-md-2 rounded-pill fw-semibold`} style={{ 
                        background: showroom.status === 'active' ? '#f0fdf4' : showroom.status === 'inactive' ? '#fef2f2' : '#fef3c7', 
                        color: showroom.status === 'active' ? '#16a34a' : showroom.status === 'inactive' ? '#dc2626' : '#92400e', 
                        fontSize: '0.75rem' 
                      }}>
                        {showroom.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="py-2 d-none d-lg-table-cell">
                      <div style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                        {showroom.storage !== undefined && showroom.storage !== null 
                          ? `${parseFloat(showroom.storage).toFixed(3)} MB`
                          : '20480.000 MB'}
                      </div>
                    </td>
                    <td className="py-2 d-none d-lg-table-cell">
                      <div style={{ fontSize: '0.875rem', color: '#1f2937' }}>
                        {showroom.storageLeft !== undefined && showroom.storageLeft !== null 
                          ? `${parseFloat(showroom.storageLeft).toFixed(3)} MB`
                          : '20480.000 MB'}
                      </div>
                    </td>
                    <td className="py-2 d-none d-xl-table-cell">
                      <div style={{ fontSize: '0.875rem', color: '#1f2937', fontWeight: '500' }}>
                        {showroom.maxUsers !== undefined && showroom.maxUsers !== null 
                          ? showroom.maxUsers
                          : '2'}
                      </div>
                    </td>
                    <td className="py-2 text-end">
                      <div className="btn-group gap-1" role="group">
                        <button 
                          className="btn btn-sm rounded-pill border-0"
                          style={{ color: '#2563eb', background: '#eff6ff', padding: '4px 6px', fontSize: '0.75rem' }}
                          title="Edit Showroom"
                          onClick={() => openEditModal(showroom)}
                        >
                          <Edit size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Showroom Modal */}
      {showAddModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', padding: '2rem', minWidth: 320, maxWidth: 600, width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#1f2937', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Add New Showroom</h4>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>Create a new showroom with all required information</p>
            </div>
            <form onSubmit={handleAddShowroom}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                {/* Basic Information */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Showroom Name *</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      name="showroomName" 
                      placeholder="Enter showroom name" 
                      value={formData.showroomName} 
                      onChange={handleInputChange} 
                      required 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Status</label>
                    <select 
                      className="form-control" 
                      name="status" 
                      value={formData.status} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended due to subscription">Suspended due to subscription</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Owner</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      name="showroomOwner" 
                      placeholder="Enter owner name" 
                      value={formData.showroomOwner} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>NTN</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      name="NTN" 
                      placeholder="Enter NTN number" 
                      value={formData.NTN} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Address</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="showroomAddress" 
                    placeholder="Enter showroom address" 
                    value={formData.showroomAddress} 
                    onChange={handleInputChange} 
                    style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Phone</label>
                    <input 
                      type="tel" 
                      className="form-control" 
                      name="showroomPhone" 
                      placeholder="Enter phone number" 
                      value={formData.showroomPhone} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>WhatsApp</label>
                    <input 
                      type="tel" 
                      className="form-control" 
                      name="whatsappNumber" 
                      placeholder="Enter WhatsApp number" 
                      value={formData.whatsappNumber} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Email</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      name="showroomEmail" 
                      placeholder="Enter email address" 
                      value={formData.showroomEmail} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Website</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="showroomWebsite" 
                      placeholder="Enter website URL" 
                      value={formData.showroomWebsite} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Logo</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'logo')}
                        style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem', fontSize: '0.875rem' }}
                      />
                      {logoFile && (
                        <div style={{ fontSize: '0.75rem', color: '#059669' }}>
                          ✓ {logoFile.name} selected
                        </div>
                      )}
                      {/* {formData.showroomLogo && !logoFile && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Current Logo:</strong>
                          </div>
                          <div style={{ 
                            width: '80px', 
                            height: '80px', 
                            background: accentLight, 
                            borderRadius: '8px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            overflow: 'hidden',
                            border: '1px solid #e2e8f0',
                            marginBottom: '0.5rem'
                          }}>
                            <img 
                              src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3020'}/api/files/presigned-download-url/${formData.showroomLogo}`}
                              alt="Current Logo" 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <div style={{ display: 'none', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                              <Building2 size={24} color={accent} />
                            </div>
                          </div>
                          <div style={{ marginBottom: '0.25rem', fontSize: '0.65rem' }}>
                            <strong>File:</strong> {getOriginalFilename(formData.showroomLogo)}
                          </div>
                          <button 
                            onClick={async () => {
                              try {
                                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3020'}/api/files/presigned-download-url/${formData.showroomLogo}`);
                                const data = await response.json();
                                if (data.presignedUrl) {
                                  window.open(data.presignedUrl, '_blank');
                                }
                              } catch (error) {
                                console.error('Error getting logo URL:', error);
                                alert('Failed to open logo');
                              }
                            }}
                            style={{ 
                              color: '#6366f1', 
                              textDecoration: 'none',
                              fontSize: '0.7rem',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0
                            }}
                          >
                            🔗 View Logo
                          </button>
                        </div>
                      )} */}
                      {uploadingLogo && (
                        <div style={{ fontSize: '0.75rem', color: '#6366f1' }}>
                          ⏳ Uploading logo...
                        </div>
                      )}
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Banner</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'banner')}
                        style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem', fontSize: '0.875rem' }}
                      />
                      {bannerFile && (
                        <div style={{ fontSize: '0.75rem', color: '#059669' }}>
                          ✓ {bannerFile.name} selected
                        </div>
                      )}
                      {formData.showroomBanner && !bannerFile && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          <div style={{ marginBottom: '0.25rem' }}>
                            <strong>Current Banner:</strong>
                          </div>
                          <div style={{ marginBottom: '0.25rem', fontSize: '0.65rem' }}>
                            <strong>File:</strong> {getOriginalFilename(formData.showroomBanner)}
                          </div>
                          <div style={{ 
                            background: '#f8fafc', 
                            padding: '0.5rem', 
                            borderRadius: '4px', 
                            border: '1px solid #e2e8f0',
                            wordBreak: 'break-all',
                            fontSize: '0.7rem',
                            fontFamily: 'monospace'
                          }}>
                            {formData.showroomBanner}
                          </div>
                          <div style={{ marginTop: '0.5rem' }}>
                            <a 
                              href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3020'}/api/files/presigned-download-url/${formData.showroomBanner}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#6366f1', 
                                textDecoration: 'none',
                                fontSize: '0.7rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              🔗 View Banner
                            </a>
                          </div>
                        </div>
                      )}
                      {uploadingBanner && (
                        <div style={{ fontSize: '0.75rem', color: '#6366f1' }}>
                          ⏳ Uploading banner...
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Description</label>
                  <textarea 
                    name="showroomDescription" 
                    rows={3}
                    placeholder="Enter showroom description" 
                    value={formData.showroomDescription} 
                    onChange={handleInputChange} 
                    style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', width: '100%', resize: 'vertical' }}
                  />
                </div>

                {/* Social Media Links */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Facebook Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="facebookLink" 
                      placeholder="Enter Facebook link" 
                      value={formData.facebookLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Instagram Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="instagramLink" 
                      placeholder="Enter Instagram link" 
                      value={formData.instagramLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Twitter Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="twitterLink" 
                      placeholder="Enter Twitter link" 
                      value={formData.twitterLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>LinkedIn Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="linkedinLink" 
                      placeholder="Enter LinkedIn link" 
                      value={formData.linkedinLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>YouTube Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="youtubeLink" 
                      placeholder="Enter YouTube link" 
                      value={formData.youtubeLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Google Maps Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="googleMapLink" 
                      placeholder="Enter Google Maps link" 
                      value={formData.googleMapLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Policy & Terms</label>
                  <textarea 
                    name="policyAndTerms" 
                    rows={3}
                    placeholder="Enter policy and terms" 
                    value={formData.policyAndTerms} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', width: '100%', resize: 'vertical' }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={handleCloseModal}
                  style={{ background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem 1.5rem', fontSize: '0.875rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  style={{ background: accent, color: '#fff', border: 'none', borderRadius: '8px', padding: '0.75rem 1.5rem', fontSize: '0.875rem', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1 }}
                >
                  {submitting ? 'Creating...' : 'Create Showroom'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Showroom Modal */}
      {showEditModal && (
        <div className="modal-backdrop" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: '12px', padding: '2rem', minWidth: 320, maxWidth: 600, width: '100%', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)', border: '1px solid #e5e7eb', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ color: '#1f2937', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Edit Showroom</h4>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>Update showroom information</p>
            </div>
            <form onSubmit={handleEditShowroom}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                {/* Basic Information */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Showroom Name *</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      name="showroomName" 
                      placeholder="Enter showroom name" 
                      value={formData.showroomName} 
                      onChange={handleInputChange} 
                      required 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Status</label>
                    <select 
                      className="form-control" 
                      name="status" 
                      value={formData.status} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="suspended due to subscription">Suspended due to subscription</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Owner</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      name="showroomOwner" 
                      placeholder="Enter owner name" 
                      value={formData.showroomOwner} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>NTN</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      name="NTN" 
                      placeholder="Enter NTN number" 
                      value={formData.NTN} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Address</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="showroomAddress" 
                    placeholder="Enter showroom address" 
                    value={formData.showroomAddress} 
                    onChange={handleInputChange} 
                    style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Phone</label>
                    <input 
                      type="tel" 
                      className="form-control" 
                      name="showroomPhone" 
                      placeholder="Enter phone number" 
                      value={formData.showroomPhone} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>WhatsApp</label>
                    <input 
                      type="tel" 
                      className="form-control" 
                      name="whatsappNumber" 
                      placeholder="Enter WhatsApp number" 
                      value={formData.whatsappNumber} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Email</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      name="showroomEmail" 
                      placeholder="Enter email address" 
                      value={formData.showroomEmail} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Website</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="showroomWebsite" 
                      placeholder="Enter website URL" 
                      value={formData.showroomWebsite} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Logo</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'logo')}
                        style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem', fontSize: '0.875rem' }}
                      />
                      {logoFile && (
                        <div style={{ fontSize: '0.75rem', color: '#059669' }}>
                          ✓ {logoFile.name} selected
                        </div>
                      )}
                      {formData.showroomLogo && !logoFile && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {/* <div style={{ marginBottom: '0.5rem' }}>
                            <strong>Current Logo:</strong>
                          </div> */}
                          {/* <div style={{ 
                            width: '80px', 
                            height: '80px', 
                            background: accentLight, 
                            borderRadius: '8px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            overflow: 'hidden',
                            border: '1px solid #e2e8f0',
                            marginBottom: '0.5rem'
                          }}> */}
                            {/* <img 
                              src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3020'}/api/files/presigned-download-url/${formData.showroomLogo}`}
                              alt="Current Logo" 
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            /> */}
                            {/* <div style={{ display: 'none', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                              <Building2 size={24} color={accent} />
                            </div> */}
                          {/* </div> */}
                          <div style={{ marginBottom: '0.25rem', fontSize: '0.65rem' }}>
                            <strong>File:</strong> {getOriginalFilename(formData.showroomLogo)}
                          </div>
                          {/* <button 
                            onClick={async () => {
                              try {
                                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3020'}/api/files/presigned-download-url/${formData.showroomLogo}`);
                                const data = await response.json();
                                if (data.presignedUrl) {
                                  window.open(data.presignedUrl, '_blank');
                                }
                              } catch (error) {
                                console.error('Error getting logo URL:', error);
                                alert('Failed to open logo');
                              }
                            }}
                            style={{ 
                              color: '#6366f1', 
                              textDecoration: 'none',
                              fontSize: '0.7rem',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: 0
                            }}
                          >
                            🔗 View Logo
                          </button> */}
                        </div>
                      )}
                      {uploadingLogo && (
                        <div style={{ fontSize: '0.75rem', color: '#6366f1' }}>
                          ⏳ Uploading logo...
                        </div>
                      )}
                    </div>
                  </div>
                  {/* <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Banner</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <input 
                        type="file" 
                        accept="image/*"
                        onChange={(e) => handleFileChange(e, 'banner')}
                        style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.5rem', fontSize: '0.875rem' }}
                      />
                      {bannerFile && (
                        <div style={{ fontSize: '0.75rem', color: '#059669' }}>
                          ✓ {bannerFile.name} selected
                        </div>
                      )}
                      {formData.showroomBanner && !bannerFile && (
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          <div style={{ marginBottom: '0.25rem' }}>
                            <strong>Current Banner:</strong>
                          </div>
                          <div style={{ marginBottom: '0.25rem', fontSize: '0.65rem' }}>
                            <strong>File:</strong> {getOriginalFilename(formData.showroomBanner)}
                          </div>
                          <div style={{ 
                            background: '#f8fafc', 
                            padding: '0.5rem', 
                            borderRadius: '4px', 
                            border: '1px solid #e2e8f0',
                            wordBreak: 'break-all',
                            fontSize: '0.7rem',
                            fontFamily: 'monospace'
                          }}>
                            {formData.showroomBanner}
                          </div>
                          <div style={{ marginTop: '0.5rem' }}>
                            <a 
                              href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3020'}/api/files/presigned-download-url/${formData.showroomBanner}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#6366f1', 
                                textDecoration: 'none',
                                fontSize: '0.7rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              🔗 View Banner
                            </a>
                          </div>
                        </div>
                      )}
                      {uploadingBanner && (
                        <div style={{ fontSize: '0.75rem', color: '#6366f1' }}>
                          ⏳ Uploading banner...
                        </div>
                      )}
                    </div>
                  </div> */}
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Description</label>
                  <textarea 
                    name="showroomDescription" 
                    rows={3}
                    placeholder="Enter showroom description" 
                    value={formData.showroomDescription} 
                    onChange={handleInputChange} 
                    style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', width: '100%', resize: 'vertical' }}
                  />
                </div>

                {/* Social Media Links */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Facebook Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="facebookLink" 
                      placeholder="Enter Facebook link" 
                      value={formData.facebookLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Instagram Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="instagramLink" 
                      placeholder="Enter Instagram link" 
                      value={formData.instagramLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Twitter Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="twitterLink" 
                      placeholder="Enter Twitter link" 
                      value={formData.twitterLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>LinkedIn Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="linkedinLink" 
                      placeholder="Enter LinkedIn link" 
                      value={formData.linkedinLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>YouTube Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="youtubeLink" 
                      placeholder="Enter YouTube link" 
                      value={formData.youtubeLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Google Maps Link</label>
                    <input 
                      type="url" 
                      className="form-control" 
                      name="googleMapLink" 
                      placeholder="Enter Google Maps link" 
                      value={formData.googleMapLink} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Policy & Terms</label>
                  <textarea 
                    name="policyAndTerms" 
                    rows={3}
                    placeholder="Enter policy and terms" 
                    value={formData.policyAndTerms} 
                      onChange={handleInputChange} 
                      style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem', width: '100%', resize: 'vertical' }}
                  />
                </div>

                {/* Storage Settings - SuperAdmin Only */}
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #e5e7eb' }}>
                  <h5 style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>Storage Settings (SuperAdmin Only)</h5>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Total Storage (MB)</label>
                      <input 
                        type="number" 
                        step="0.001"
                        className="form-control" 
                        name="storage" 
                        placeholder="20480.000" 
                        value={formData.storage} 
                        onChange={handleInputChange} 
                        style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                      />
                      <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Total storage allocated in MB</small>
                    </div>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Storage Left (MB)</label>
                      <input 
                        type="number" 
                        step="0.001"
                        className="form-control" 
                        name="storageLeft" 
                        placeholder="20480.000" 
                        value={formData.storageLeft} 
                        onChange={handleInputChange} 
                        style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                      />
                      <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Remaining storage in MB</small>
                    </div>
                  </div>
                  <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#fef3c7', borderRadius: '8px', border: '1px solid #fde68a' }}>
                    <div style={{ fontSize: '0.75rem', color: '#92400e', lineHeight: '1.5' }}>
                      <strong>Note:</strong> If you update Total Storage without updating Storage Left, Storage Left will be adjusted proportionally. Storage Left cannot exceed Total Storage.
                    </div>
                  </div>
                </div>

                {/* User Limit Settings - SuperAdmin Only */}
                {currentUser && currentUser.role === 'superAdmin' && (
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '2px solid #e5e7eb' }}>
                    <h5 style={{ color: '#374151', fontSize: '0.875rem', fontWeight: '600', marginBottom: '0.75rem' }}>User Limit Settings (SuperAdmin Only)</h5>
                    <div>
                      <label style={{ display: 'block', color: '#374151', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>Maximum Users</label>
                      <input 
                        type="number" 
                        min="1"
                        className="form-control" 
                        name="maxUsers" 
                        placeholder="2" 
                        value={formData.maxUsers} 
                        onChange={handleInputChange} 
                        style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', fontSize: '0.875rem' }}
                      />
                      <small style={{ color: '#6b7280', fontSize: '0.75rem' }}>Maximum number of users allowed for this showroom</small>
                      <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                        <div style={{ fontSize: '0.75rem', color: '#1e40af', lineHeight: '1.5' }}>
                          <strong>Note:</strong> The maximum users limit cannot be set below the current number of active users in the showroom.
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button 
                  type="button" 
                  onClick={handleCloseModal}
                  style={{ background: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem 1.5rem', fontSize: '0.875rem', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={submitting}
                  style={{ background: accent, color: '#fff', border: 'none', borderRadius: '8px', padding: '0.75rem 1.5rem', fontSize: '0.875rem', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1 }}
                >
                  {submitting ? 'Updating...' : 'Update Showroom'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Showrooms;
